// Markdown parser utility for rendering knowledge base articles

// Process inline markdown (bold, code, etc.)
export const processInlineMarkdown = (text: string): string => {
  if (!text) return ''
  
  // Convert bold first (before italic to avoid conflicts)
  // Match **text** - at least one character between the asterisks
  text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
  
  // Convert inline code
  text = text.replace(/`([^`]+?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
  
  // Convert italic (single asterisk)
  // Since we processed bold first, remaining single asterisks should be safe
  // Match *text* where text doesn't contain asterisks
  text = text.replace(/\*([^*]+?)\*/g, '<em>$1</em>')
  
  return text
}

// Render list items
export const renderList = (items: string[]): string => {
  if (items.length === 0) return ''
  return `<ul class="list-disc list-inside mb-4 space-y-1 ml-4">${items.map(item => `<li>${item}</li>`).join('')}</ul>`
}

// Format content - markdown rendering
export const formatMarkdown = (content: string): string => {
  if (!content || typeof content !== 'string') return ''
  
  // Split into lines for better processing
  const lines = content.split('\n')
  const output: string[] = []
  let inList = false
  let listItems: string[] = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()
    
    // Headers - check in order from most specific to least specific
    if (trimmed.startsWith('### ')) {
      if (inList) {
        output.push(renderList(listItems))
        listItems = []
        inList = false
      }
      output.push(`<h3 class="text-lg font-semibold mt-4 mb-2">${trimmed.substring(4)}</h3>`)
      continue
    }
    if (trimmed.startsWith('## ')) {
      if (inList) {
        output.push(renderList(listItems))
        listItems = []
        inList = false
      }
      output.push(`<h2 class="text-xl font-bold mt-6 mb-3">${trimmed.substring(3)}</h2>`)
      continue
    }
    if (trimmed.startsWith('# ') && trimmed.length > 2) {
      if (inList) {
        output.push(renderList(listItems))
        listItems = []
        inList = false
      }
      output.push(`<h1 class="text-2xl font-bold mt-6 mb-4">${trimmed.substring(2)}</h1>`)
      continue
    }
    
    // List items (unordered) - match both - and * with required space after
    // This matches: "- text" or "* text" but not "-text" or "*text"
    if (trimmed.match(/^[-*]\s+.*/)) {
      if (!inList) {
        inList = true
      }
      // Remove the list marker (- or *) and the space after it
      const itemText = trimmed.replace(/^[-*]\s+/, '').trim()
      if (itemText) {
        listItems.push(processInlineMarkdown(itemText))
      }
      continue
    }
    
    // List items (ordered)
    if (trimmed.match(/^\d+\.\s+/)) {
      if (!inList) {
        inList = true
      }
      const itemText = trimmed.replace(/^\d+\.\s+/, '')
      listItems.push(processInlineMarkdown(itemText))
      continue
    }
    
    // Empty line or regular text
    if (trimmed === '') {
      if (inList) {
        output.push(renderList(listItems))
        listItems = []
        inList = false
      }
      output.push('')
      continue
    }
    
    // Regular paragraph
    if (inList) {
      output.push(renderList(listItems))
      listItems = []
      inList = false
    }
    output.push(`<p class="mb-4">${processInlineMarkdown(trimmed)}</p>`)
  }
  
  // Close any open list
  if (inList) {
    output.push(renderList(listItems))
  }
  
  return output.join('\n')
}
